import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  FolderIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { fetchHseSetupStatus } from "../../../api/hse/setupStatus";
import { hseKsSetupStatus } from "./hseKsSetupStatus";
import { DeviationDeliveryHealth } from "../quality/deviations/DeviationDeliveryHealth";

const GuideTaskRow = ({ icon: Icon, title, status, description, to, linkLabel }) => {
  const tone =
    status === "ok"
      ? "border-emerald-200 bg-emerald-50/50"
      : status === "warn"
        ? "border-amber-200 bg-amber-50/40"
        : "border-slate-200 bg-slate-50/60";

  const StatusIcon =
    status === "ok" ? CheckCircleIcon : status === "warn" ? ExclamationTriangleIcon : InformationCircleIcon;

  const statusColor =
    status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-800" : "text-slate-600";

  return (
    <div className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${tone}`}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${statusColor}`}>
            <StatusIcon className="h-4 w-4 shrink-0" />
            <span>{description}</span>
          </div>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
        >
          {linkLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
};

export const HseKsGuideSection = () => {
  const { t } = useTranslation();
  const [setup, setSetup] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchHseSetupStatus();
        if (!cancelled) {
          setSetup(data);
          setLoadError(false);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setSetup(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const status = useMemo(() => hseKsSetupStatus(setup || {}), [setup]);
  const issueKeys = status.issues.map((item) => item.key);

  const statusFor = (key, { info = false } = {}) => {
    if (loadError) return "warn";
    if (!setup) return "info";
    if (issueKeys.includes(key)) return info ? "info" : "warn";
    return "ok";
  };

  return (
    <section id="hse-ks-setup-status" className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-slate-900">{t("company_guide.hse.title")}</h2>
      <p className="mt-1 text-sm text-slate-600">{t("company_guide.hse.subtitle")}</p>
      {loadError ? (
        <p className="mt-3 text-sm text-amber-800">{t("company_guide.hse.progress_error")}</p>
      ) : null}
      <div className="mt-4 space-y-2">
        <GuideTaskRow
          icon={ShieldCheckIcon}
          title={t("company_guide.hse.task.responsible.title")}
          status={statusFor("responsible")}
          description={t(
            issueKeys.includes("responsible")
              ? "company_guide.hse.task.responsible.warn"
              : "company_guide.hse.task.responsible.ok"
          )}
          to="/dashboard/hse-ks/settings?tab=module"
          linkLabel={t("company_guide.hse.link.settings")}
        />
        <GuideTaskRow
          icon={UserGroupIcon}
          title={t("company_guide.hse.task.verneombud.title")}
          status={statusFor("verneombud")}
          description={t(
            issueKeys.includes("verneombud")
              ? "company_guide.hse.task.verneombud.warn"
              : "company_guide.hse.task.verneombud.ok"
          )}
          to="/dashboard/hse-ks/settings?tab=safety_rep"
          linkLabel={t("company_guide.hse.link.settings")}
        />
        <GuideTaskRow
          icon={FolderIcon}
          title={t("company_guide.hse.task.categories.title")}
          status={statusFor("categories", { info: true })}
          description={t(
            issueKeys.includes("categories")
              ? "company_guide.hse.task.categories.info"
              : "company_guide.hse.task.categories.ok"
          )}
          to="/dashboard/hse-ks/settings?tab=risk"
          linkLabel={t("company_guide.hse.link.settings")}
        />
        <GuideTaskRow
          icon={SparklesIcon}
          title={t("company_guide.hse.task.ai.title")}
          status={statusFor("ai_key", { info: true })}
          description={t(
            issueKeys.includes("ai_key")
              ? "company_guide.hse.task.ai.warn"
              : "company_guide.hse.task.ai.info"
          )}
          to="/dashboard/hse-ks/settings?tab=ai"
          linkLabel={t("company_guide.hse.link.settings")}
        />
        <GuideTaskRow
          icon={FlagIcon}
          title={t("company_guide.hse.task.goals.title")}
          status="info"
          description={t("company_guide.hse.task.goals.info")}
          to="/dashboard/hse-ks/goals"
          linkLabel={t("company_guide.hse.link.goals")}
        />
      </div>
      <div className="mt-4">
        <DeviationDeliveryHealth health={setup?.deviation_notifications} />
        <Link to="/dashboard/hse-ks/settings?tab=deviation_notifications" className="mt-2 inline-block text-sm font-semibold text-blue-700 underline">
          {t("company_guide.hse.link.settings")}
        </Link>
      </div>
    </section>
  );
};
